import { ChangeEvent, FocusEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles, convertBase64, createSyntheticEvent, ErrorText } from 'vopay';
import { CircleButton } from '#shared/components/UI';
import styles from './BrandingSettingsForm.module.scss';

const cn = withStyles(styles);

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

type BrandingLogoFieldProps = {
  name: string;
  value: string;
  disabled: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur: (event: FocusEvent<HTMLInputElement>) => void;
  error?: string | null;
};

export default function BrandingLogoField({ name, value, disabled, onChange, onBlur, error }: BrandingLogoFieldProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Branding.Form' });
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();

    if (file.type !== 'image/svg+xml' && extension !== 'svg') {
      setFileError(t('invalidFileFormat', { defaultValue: 'File format is invalid. Please upload a SVG file.' }));

      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError(t('fileTooLarge', { defaultValue: 'Max 20MB allowed. Please try again.' }));

      return;
    }

    setFileError(null);
    const base64 = (await convertBase64(event.target)) as string;

    onChange(createSyntheticEvent(name, base64));
  }

  function handleDelete() {
    setFileError(null);
    onChange(createSyntheticEvent(name, ''));
  }

  return (
    <div className={cn('fieldWrapper')}>
      <div className={cn('fieldBox', 'logoFieldBox')}>
        {value && <img className={cn('logoFieldImage')} src={value} alt="logo" />}

        {!disabled && (
          <div className={cn('logoFieldActions')}>
            <CircleButton
              colorScheme="action"
              size="medium"
              icon="file_upload"
              aria-label={t('uploadLogo', { defaultValue: 'Upload Logo' })}
              onClick={() => inputRef.current?.click()}
            />

            <CircleButton
              colorScheme="destructive"
              size="medium"
              icon="delete"
              aria-label={t('deleteLogo', { defaultValue: 'Delete logo' })}
              disabled={!value}
              onClick={handleDelete}
            />
          </div>
        )}
      </div>

      <ErrorText error={error || fileError} size="sm" />

      <input
        ref={inputRef}
        type="file"
        name={name}
        accept="image/svg+xml"
        className={cn('hiddenFileInput')}
        onChange={handleFileChange}
        onBlur={onBlur}
      />
    </div>
  );
}
